/* eslint-disable import/no-anonymous-default-export */
import axios from './axios';
import {
  CollectionResult,
  timeSpanToSeconds,
  EventLineupEntry,

  Period,
  Session,
  SessionEvent,
  TimeSpan,
  Video,
} from './sessionModels';

const URL = process.env.REACT_APP_PORTAL_URL;
const SESSIONS = `${URL}/api/v1/sessions`;

export type SessionInput = {
  pitchId?: number | null;
  pipelineId?: number | null;
  competition?: string | null;
  matchStartTime?: string | null;
  keepData?: boolean | null;
  playerCount?: number | null;
  keeperCount?: number | null;
  homeKeeperColor?: string | null;
  homePlayerColor?: string | null;
  awayKeeperColor?: string | null;
  awayPlayerColor?: string | null;
  refereeColor?: string | null;
  homeTeamId?: string | null;
  awayTeamId?: string | null;
  label?: string | null;
  homeScore?: number | null;
  awayScore?: number | null;
  startAndFinishOffset?: number | null;
  matchInfoUrl?: string | null;
  isFullId?: boolean | null;
  labelAccountId?: string | null;
  transcodeBackend?: string | null;
  approved?: boolean;
  syncInfo?: string | null;
  accountId?: string | null;
};

export type PeriodInput = {
  type: string;
  label?: string | null;
  startTime?: TimeSpan | null;
  endTime?: TimeSpan | null;
  teamLeftId?: string | null;
};

export type EventInput = {
  reason?: string | null;
  timestamp: TimeSpan;
  type: string;
  in?: EventLineupEntry | null;
  out?: EventLineupEntry | null;
  teamId?: string | null;
  offscreen: boolean;
};

export type LineupEntryInput = {
  id?: number | null;
  inSeconds: number;
  outSeconds?: number | null;
  shirtNumber: number;
  periodIn?: string | null;
  periodOut?: string | null;
  type?: string | null;
  playerId?: string | null;
  substitutedId?: string | null;
  reason?: string | null;
  inEventId?: number | null;
  outEventId?: number | null;
};

export type VideoInput = {
  url: string;
  calibration?: string | null;
  sortOrder: number;
  clickPoints?: string | null;
  syncOffset: TimeSpan;
};

export const toSessionInput = (session: Session): SessionInput => ({
  pitchId: session.pitch?.id ?? null,
  pipelineId: session.pipelineId ?? null,
  competition: session.competition,
  matchStartTime: session.matchStartTime,
  keepData: session.keepData,
  playerCount: session.playerCount,
  keeperCount: session.keeperCount,
  homeKeeperColor: session.homeKeeperColor,
  homePlayerColor: session.homePlayerColor,
  awayKeeperColor: session.awayKeeperColor,
  awayPlayerColor: session.awayPlayerColor,
  refereeColor: session.refereeColor,
  homeTeamId: session.homeTeam?.id ?? null,
  awayTeamId: session.awayTeam?.id ?? null,
  label: session.label,
  homeScore: session.homeScore,
  awayScore: session.awayScore,
  startAndFinishOffset: timeSpanToSeconds(session.startAndFinishOffset),
  matchInfoUrl: session.matchInfoUrl,
  isFullId: session.isFullId,
  labelAccountId: session.labelAccount?.id ?? null,
  transcodeBackend: session.transcodeBackend,
  approved: session.approved,
  syncInfo: session.syncInfo,
});

export const updateSessionFields = (session: Session, update: SessionInput): Promise<Session> =>
  axios.put(`${SESSIONS}/${session.id}`, { ...toSessionInput(session), ...update });

export default {
  listArtifacts(id: number | string) {
    return axios.get(`${SESSIONS}/${id}/artifacts`);
  },
  downloadFile(id: number | string, filename: string) {
    return axios.get(`${SESSIONS}/${id}/artifacts/${encodeURIComponent(filename)}`);
  },
  startSessionRun(params: any) {
    return axios.post(`${URL}/api/v1/runs`, params);
  },
  getSession(id: number | string): Promise<Session> {
    return axios.get(`${SESSIONS}/${id}`);
  },
  getSessions(params?: any): Promise<CollectionResult<Session>> {
    return axios.get(SESSIONS, { params });
  },
  approveSession(id: number | string) {
    return axios.post(`${SESSIONS}/${id}/approve`, { approved: true });
  },
  exportSession(id: number | string, isPythonLa: boolean) {
    return axios.post(`${SESSIONS}/${id}/export?isPythonLa=${isPythonLa}`);
  },
  exportSessionFile(id: number | string) {
    return axios.downloadAndSaveFile(`${SESSIONS}/${id}/export`);
  },
  duplicateSession(id: number | string, label?: string) {
    return axios.post(`${SESSIONS}/${id}/duplicate`, { label });
  },
  duplicateSessions(sessionIds: number[], label?: string) {
    return axios.post(`${SESSIONS}/duplicate`, { sessionIds, label });
  },
  createSession(input: SessionInput): Promise<Session> {
    return axios.post(SESSIONS, input);
  },
  updateSession(id: number | string, input: SessionInput): Promise<Session> {
    return axios.put(`${SESSIONS}/${id}`, input);
  },
  deleteSession(id: number | string) {
    return axios.delete(`${SESSIONS}/${id}`);
  },

  addPeriod(sessionId: number | string, input: PeriodInput): Promise<Period> {
    return axios.post(`${SESSIONS}/periods`, { ...input, sessionId: Number(sessionId) });
  },
  updatePeriod(id: number, input: PeriodInput): Promise<Period> {
    return axios.put(`${SESSIONS}/periods/${id}`, input);
  },
  deletePeriod(id: number) {
    return axios.delete(`${SESSIONS}/periods/${id}`);
  },

  addEvent(sessionId: number | string, input: EventInput): Promise<SessionEvent> {
    return axios.post(`${SESSIONS}/${sessionId}/events`, input);
  },
  updateEvent(sessionId: number | string, id: number, input: EventInput): Promise<SessionEvent> {
    return axios.put(`${SESSIONS}/${sessionId}/events/${id}`, input);
  },
  deleteEvent(sessionId: number | string, id: number) {
    return axios.delete(`${SESSIONS}/${sessionId}/events/${id}`);
  },

  upsertLineup(sessionId: number | string, lineupEntries: LineupEntryInput[]): Promise<void> {
    return axios.put(`${SESSIONS}/${sessionId}/lineup-entries`, { lineupEntries });
  },

  addVideo(sessionId: number | string, input: VideoInput): Promise<Video> {
    return axios.post(`${SESSIONS}/videos`, { ...input, sessionId: Number(sessionId) });
  },
  updateVideo(id: number, input: VideoInput): Promise<Video> {
    return axios.put(`${SESSIONS}/videos/${id}`, input);
  },
  deleteVideo(id: number) {
    return axios.delete(`${SESSIONS}/videos/${id}`);
  },
  getVideo(id: number | string): Promise<Video> {
    return axios.get(`${SESSIONS}/videos/${id}`);
  },
  updateVideoOrder(videoIds: number[]) {
    return axios.put(`${SESSIONS}/videos/sortOrder`, { sortOrder: videoIds });
  },
};
